import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'

/**
 * Register a team for a tournament category via Cloud Function
 * @param {Object} params - Registration parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {string} params.categoryId - Category ID
 * @param {Object} params.player1 - Player 1 data
 * @param {Object} params.player2 - Player 2 data (optional for singles)
 * @param {string} params.userId - User ID of the person registering (optional, derived from auth)
 * @param {string} params.userEmail - Email of the person registering (optional, derived from auth)
 * @param {string} params.userName - Full name of the person registering (optional)
 * @param {File} params.paymentProof - Payment proof image file
 * @returns {Promise<{success: boolean, teamId?: string, error?: string}>}
 */
export const registerTeam = async ({ tournamentId, categoryId, player1, player2, userId, userEmail, userName, paymentProof }) => {
  try {
    // Prepare payment proof data if provided
    let paymentProofData = null
    if (paymentProof) {
      // Convert file to base64 for Cloud Function
      const reader = new FileReader()
      const base64Data = await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1] // Remove data:image/xxx;base64, prefix
          resolve(base64)
        }
        reader.onerror = reject
        reader.readAsDataURL(paymentProof)
      })

      paymentProofData = {
        data: base64Data,
        name: paymentProof.name,
        type: paymentProof.type
      }
    }

    // Call Cloud Function
    const submitRegistration = httpsCallable(functions, 'submitRegistration')
    const result = await submitRegistration({
      tournamentId,
      categoryId,
      player1,
      player2: player2 || null,
      paymentProofData
    })

    return {
      success: true,
      teamId: result.data.teamId
    }
  } catch (error) {
    console.error('Error registering team:', error)
    
    // Extract user-friendly error message
    let errorMessage = 'Failed to register team'
    if (error.code === 'unauthenticated') {
      errorMessage = 'You must be signed in to register'
    } else if (error.code === 'invalid-argument') {
      errorMessage = error.message
    } else if (error.message) {
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage
    }
  }
}
